import dayjs from 'dayjs'
import type { TPayInfo } from './interface'
import { ModalCollection } from '~/constants'
import { useModalInformation } from '~/composables/common/use-popup-information'
import { useApi } from '~/composables/common/use-api'
import { DEPOSIT_WITHDRAW_API } from '~/configs/api'
import { ResponseStatus } from '~/composables/common/interface'
import { usePromotion } from '~/composables/promotion/use-promotion'
import { notificationPopup } from '~utils/modal/notify'
import { modalList } from '~/resources/popup-notification'
import { useCommon } from '~/composables/common/use-common'

interface QrFormData {
  depositAmount: string
  packageId: string
}

const INITIAL_CLOSE_QR_COUNTDOWN = 60

export function useTpay() {
  const { depositConfirmCancelPromotion } = usePromotion()
  const { openModal, closeModalInfo } = useModalInformation()
  const { request } = useApi()
  const { openLiveChat } = useCommon()

  const formData = reactive<QrFormData>({
    depositAmount: '',
    packageId: ''
  })

  const dataInfo = ref<TPayInfo>({
    amount: '0',
    bank_name: '',
    bank_code: '',
    bank_account_name: '',
    bank_account_no: '',
    qr_code: '',
    qr_code_note: '',
    expired_time: ''
  })

  const expiredQrCountdown = ref('')
  const expiredQrCountdownInterval = ref<NodeJS.Timeout>()
  const closeQrCountdown = ref(0)
  const closeQrCountdownInterval = ref<NodeJS.Timeout>()
  const isTpayMaintenance = ref(false)

  const setExpiredQrCountdown = () => {
    clearInterval(expiredQrCountdownInterval.value)
    expiredQrCountdownInterval.value = setInterval(() => {
      const timeLeftInSeconds = dayjs(dataInfo.value.expired_time).diff(dayjs(), 's')
      if (timeLeftInSeconds <= 0) {
        closeModalInfo(ModalCollection.DEPOSIT_TPAY_MODAL)
        clearInterval(expiredQrCountdownInterval.value)
        return
      }
      const mm = Math.floor(timeLeftInSeconds / 60)
        .toString()
        .padStart(2, '0')
      const ss = (timeLeftInSeconds % 60).toString().padStart(2, '0')
      expiredQrCountdown.value = `${mm}:${ss}`
    }, 1000)
  }

  const setCloseQrCountdown = () => {
    clearInterval(closeQrCountdownInterval.value)
    closeQrCountdown.value = INITIAL_CLOSE_QR_COUNTDOWN
    closeQrCountdownInterval.value = setInterval(() => {
      closeQrCountdown.value -= 1
      if (closeQrCountdown.value === 0) {
        clearInterval(closeQrCountdownInterval.value)
      }
    }, 1000)
  }

  async function handleSubmit(): Promise<void> {
    const payload = {
      amount: NumberUtils.formatMarkToNumber(formData.depositAmount, 1000),
      package_id: formData.packageId
    }
    try {
      const { data, status } = await request(DEPOSIT_WITHDRAW_API.DEPOSIT_TPAY, {
        method: 'POST',
        body: payload
      })
      if (status === ResponseStatus.INVALID_PACKAGE_ID_INVOICE_ERROR) {
        depositConfirmCancelPromotion()
        return
      }
      if (status === ResponseStatus.OK) {
        dataInfo.value = {
          ...data,
          amount: NumberUtils.formatNumberWithDots(NumberUtils.formatMarkToNumber(formData.depositAmount, 1000))
        }
        openModal(ModalCollection.DEPOSIT_TPAY_MODAL)
        setExpiredQrCountdown()
        setCloseQrCountdown()
        return
      }
      if (status === ResponseStatus.OVER_RATE_LIMIT_PER_DAY_TPAY || status === ResponseStatus.LIMIT_REQUEST) {
        notificationPopup({
          ...modalList.LIMIT_REQUEST,
          confirmCallback: openLiveChat
        })
        return
      }
      notificationPopup(modalList.DEPOSIT_ERROR)
    } catch {
      notificationPopup(modalList.DEPOSIT_ERROR)
    }
  }

  const checkTpayStatus = async () => {
    try {
      const { data, status } = await request(`${DEPOSIT_WITHDRAW_API.TPAY_STATUS}`, {
        method: 'GET'
      })
      if (status === ResponseStatus.OK) {
        isTpayMaintenance.value = data?.is_maintain || false
      }
    } catch {
      isTpayMaintenance.value = false
    }
  }

  onUnmounted(() => {
    clearInterval(expiredQrCountdownInterval.value)
    clearInterval(closeQrCountdownInterval.value)
  })

  return {
    formData,
    dataInfo,
    isTpayMaintenance,
    handleSubmit,
    closeQrCountdown,
    expiredQrCountdown,
    checkTpayStatus
  }
}
